/**
 * Recipes Store - Beredskapsrecept
 * Hanterar användarens beredskapsrecept med ingredienser och instruktioner
 */

import { create } from 'zustand';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useFamilyStore } from './family-store';
import { logActivity } from '@/lib/activity-logger';

export interface RecipeIngredient {
  id: string;
  item: string; // Namnet på ingrediensen
  quantity: number;
  unit: string; // t.ex. "kg", "liter", "st", "msk"
  order: number;
}

export interface RecipeInstruction {
  id: string;
  step: number;
  text: string;
}

export interface Recipe {
  id: string;
  userId: string;
  dish: string; // Maträtt (max 50 tecken)
  description: string; // Max 500 tecken
  servings: number; // Standardantal portioner
  ingredients: RecipeIngredient[];
  instructions: RecipeInstruction[];
  tags: string[];
  isPinned: boolean;
  isPrivate: boolean; // Privata recept sparas alltid i användarens egen profil
  authorId: string;
  authorName: string;
  lastModifiedBy?: string;
  lastModifiedById?: string;
  createdAt: string;
  updatedAt: string;
}

interface RecipesState {
  recipes: Recipe[];
  totalCount: number;
  loadedScopeKey: string | null;
  
  // Actions
  fetchFromServer: (scopeKey: string) => Promise<boolean>;
  clearRecipes: () => void;
  addRecipe: (recipe: Omit<Recipe, 'id' | 'userId' | 'authorId' | 'authorName' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateRecipe: (recipeId: string, updates: Partial<Pick<Recipe, 'dish' | 'description' | 'servings' | 'ingredients' | 'instructions' | 'tags' | 'isPinned'>>) => Promise<void>;
  deleteRecipe: (recipeId: string) => Promise<void>;
  togglePin: (recipeId: string) => Promise<void>;
  
  // Selectors
  getRecipeById: (recipeId: string) => Recipe | undefined;
  getRecipesByTag: (tag: string) => Recipe[];
  searchRecipes: (query: string) => Recipe[];
  getAllTags: () => string[];
}

export const useRecipesStore = create<RecipesState>()((set, get) => ({
  recipes: [],
  totalCount: 0,
  loadedScopeKey: null,

  fetchFromServer: async (scopeKey) => {
    try {
      const familyId = useFamilyStore.getState().family?.id;
      const url = familyId ? `/recipes?familyId=${familyId}` : '/recipes';
      const res = await apiGet<{ recipes: Recipe[]; totalCount: number }>(url);
      if (res.success && res.data) {
        set({ recipes: res.data.recipes, totalCount: res.data.totalCount, loadedScopeKey: scopeKey });
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to fetch recipes:', error);
      return false;
    }
  },

  clearRecipes: () => {
    set({ recipes: [], totalCount: 0, loadedScopeKey: null });
  },

  addRecipe: async (recipe) => {
    try {
      const familyId = useFamilyStore.getState().family?.id;
      const url = familyId ? `/recipes?familyId=${familyId}` : '/recipes';
      const res = await apiPost<Recipe>(url, recipe);
      if (res.success && res.data) {
        logActivity({ action: 'CREATE_RECIPE', category: 'recipes', description: 'Recipe created', metadata: { recipeId: res.data.id, dish: recipe.dish } });
        set((state) => ({ 
          recipes: [res.data as Recipe, ...state.recipes],
          totalCount: state.totalCount + 1,
        }));
      }
    } catch (error) {
      console.error('Failed to add recipe:', error);
      throw error;
    }
  },

  updateRecipe: async (recipeId, updates) => {
    try {
      const familyId = useFamilyStore.getState().family?.id;
      const url = familyId ? `/recipes/${recipeId}?familyId=${familyId}` : `/recipes/${recipeId}`;
      const res = await apiPut<Recipe>(url, updates);
      if (res.success && res.data) {
        logActivity({ action: 'UPDATE_RECIPE', category: 'recipes', description: 'Recipe updated', metadata: { recipeId } });
        set((state) => ({
          recipes: state.recipes.map((r) => (r.id === recipeId ? res.data as Recipe : r)),
        }));
      }
    } catch (error) {
      console.error('Failed to update recipe:', error);
      throw error;
    }
  },

  deleteRecipe: async (recipeId) => {
    try {
      const familyId = useFamilyStore.getState().family?.id;
      const url = familyId ? `/recipes/${recipeId}?familyId=${familyId}` : `/recipes/${recipeId}`;
      const res = await apiDelete(url);
      if (res.success) {
        logActivity({ action: 'DELETE_RECIPE', category: 'recipes', description: 'Recipe deleted', metadata: { recipeId } });
        set((state) => ({ 
          recipes: state.recipes.filter((r) => r.id !== recipeId),
          totalCount: Math.max(0, state.totalCount - 1),
        }));
      }
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      throw error;
    }
  },

  togglePin: async (recipeId) => {
    try {
      const recipe = get().recipes.find((r) => r.id === recipeId);
      if (!recipe) return;

      const familyId = useFamilyStore.getState().family?.id;
      const url = familyId ? `/recipes/${recipeId}?familyId=${familyId}` : `/recipes/${recipeId}`;
      
      const res = await apiPut(url, { isPinned: !recipe.isPinned });
      if (res.success && res.data) {
        set((state) => ({
          recipes: state.recipes.map((r) => (r.id === recipeId ? res.data as Recipe : r)),
        }));
      }
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      throw error;
    }
  },

  getRecipeById: (recipeId) => {
    return get().recipes.find((r) => r.id === recipeId);
  },

  getRecipesByTag: (tag) => {
    return get().recipes.filter((r) => r.tags.includes(tag));
  },

  searchRecipes: (query) => {
    const lowerQuery = query.toLowerCase();
    return get().recipes.filter(
      (r) =>
        r.dish.toLowerCase().includes(lowerQuery) ||
        r.description.toLowerCase().includes(lowerQuery) ||
        r.ingredients.some((ing) => ing.item.toLowerCase().includes(lowerQuery)) ||
        r.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
    );
  },

  getAllTags: () => {
    const allTags = get().recipes.flatMap((r) => r.tags);
    return Array.from(new Set(allTags)).sort();
  },
}));

// Hydration hook - always returns true since we don't persist
export function useRecipesHydrated() {
  return true;
}
